import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

export interface Ticket {
  id: string;
  customer: string;
  email: string;
  phone: string;
  category: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed' | 'on-hold';
  priority: 'low' | 'medium' | 'high';
  date: string;
  description: string;
  assignedTo?: string;
  make?: string;
  model?: string;
  serialNumber?: string;
  preferredDate?: string;
  preferredTime?: string;
  resolution?: string;
  closedDate?: string;
  price?: number;
  notes?: string[];
}

interface TicketStore {
  tickets: Ticket[];
  addTicket: (ticket: Omit<Ticket, 'id' | 'status' | 'date'>) => Promise<void>;
  updateTicket: (id: string, updates: Partial<Ticket>) => Promise<void>;
  deleteTicket: (id: string) => Promise<void>;
  fetchTickets: () => Promise<void>;
  getStats: () => {
    activeTickets: number;
    completedTickets: number;
    revenue: number;
    customerSatisfaction: number;
    avgResponseTime: string;
    ticketGrowth: number;
    revenueGrowth: number;
  };
}

export const useTicketStore = create<TicketStore>()((set, get) => ({
  tickets: [],
  
  fetchTickets: async () => {
    try {
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ tickets: data || [] });
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to fetch tickets');
    }
  },

  addTicket: async (ticketData) => {
    try {
      const newTicket = {
        ...ticketData,
        status: 'open' as const,
        date: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('tickets')
        .insert([newTicket])
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        tickets: [data, ...state.tickets]
      }));
    } catch (error) {
      console.error('Failed to add ticket:', error);
      throw error;
    }
  },

  updateTicket: async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('tickets')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        tickets: state.tickets.map(ticket =>
          ticket.id === id ? { ...ticket, ...data } : ticket
        )
      }));
    } catch (error) {
      console.error('Failed to update ticket:', error);
      throw error;
    }
  },

  deleteTicket: async (id) => {
    try {
      const { error } = await supabase
        .from('tickets')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        tickets: state.tickets.filter(ticket => ticket.id !== id)
      }));
    } catch (error) {
      console.error('Failed to delete ticket:', error);
      throw error;
    }
  },

  getStats: () => {
    const tickets = get().tickets;
    const now = new Date();
    const lastMonth = new Date(now.setMonth(now.getMonth() - 1));

    const activeTickets = tickets.filter(t => t.status === 'open' || t.status === 'in-progress').length;
    const completedTickets = tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length;
    
    const currentRevenue = tickets
      .filter(t => t.status === 'resolved' || t.status === 'closed')
      .reduce((sum, t) => sum + (t.price || 0), 0);

    const lastMonthTickets = tickets.filter(t => 
      new Date(t.date) >= lastMonth && 
      (t.status === 'resolved' || t.status === 'closed')
    );

    const lastMonthRevenue = lastMonthTickets.reduce((sum, t) => sum + (t.price || 0), 0);
    const customerSatisfaction = 4.8;
    
    const responseTimes = tickets
      .filter(t => t.status !== 'open')
      .map(t => new Date(t.date).getTime())
      .sort();

    const avgResponse = responseTimes.length > 0
      ? Math.floor((responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length) / (1000 * 60 * 60))
      : 2.5;

    return {
      activeTickets,
      completedTickets,
      revenue: currentRevenue,
      customerSatisfaction,
      avgResponseTime: `${avgResponse} hours`,
      ticketGrowth: Math.floor((activeTickets / Math.max(1, lastMonthTickets.length) - 1) * 100),
      revenueGrowth: Math.floor((currentRevenue / Math.max(1, lastMonthRevenue) - 1) * 100)
    };
  }
}));

// Initialize ticket fetching
useTicketStore.getState().fetchTickets();